import React, { useState } from "react";
import { adminLogin } from "../../../State/AuthSlice";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { Button, CircularProgress, Paper, TextField, Alert, IconButton, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import api from "../../../config/Api";

const AdminLoginForm = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { auth } = useAppSelector((store) => store);
    const [showPassword, setShowPassword] = useState(false);
    
    // Reset Password Modal State
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [resetEmail, setResetEmail] = useState("");
    const [resetMessage, setResetMessage] = useState<{ text: string; severity: "success" | "error" } | null>(null);
    const [resetLoading, setResetLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            email: "",
            password: "",
        },
        validationSchema: Yup.object({
            email: Yup.string().email("Invalid email").required("Admin Email / Username is required"),
            password: Yup.string().required("Password is required"),
        }),
        onSubmit: (values) => {
            const formattedPayload = {
                email: values.email.trim(),
                password: values.password,
            };

            dispatch(adminLogin({ loginRequest: formattedPayload, navigate }));
        },
    });

    const handleInitiatePasswordReset = async () => {
        if (!resetEmail || !resetEmail.includes("@")) {
            setResetMessage({ text: "Please enter a valid email address.", severity: "error" });
            return;
        }

        setResetLoading(true);
        setResetMessage(null);

        try {
            await api.post("/auth/admin/reset-password", { email: resetEmail.trim() });
            setResetMessage({
                text: "A new temporary 10-character password has been generated and sent to your email.",
                severity: "success",
            });
        } catch (error: any) {
            setResetMessage({
                text: error.response?.data?.message || "Failed to reset password. Ensure the email is a valid Admin account.",
                severity: "error",
            });
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <div className="w-full min-h-[80vh] flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
            <Paper elevation={3} className="w-full max-w-md p-6 sm:p-8 rounded-xl space-y-6">
                <h1 className="text-center font-bold text-2xl sm:text-3xl text-primary-color pb-2">
                    Login As Admin
                </h1>

                {auth.error && (
                    <Alert severity="error" className="mb-2">
                        {auth.error}
                    </Alert>
                )}

                <form onSubmit={formik.handleSubmit} className="space-y-5">
                    <TextField
                        fullWidth
                        id="email"
                        name="email"
                        label="Admin Email"
                        variant="outlined"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                    />

                    <TextField
                        fullWidth
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        label="Password"
                        variant="outlined"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                        helperText={formik.touched.password && formik.errors.password}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={() => {
                                setResetMessage(null);
                                setResetEmail("");
                                setOpenResetDialog(true);
                            }}
                            className="text-xs text-blue-600 hover:underline font-semibold"
                        >
                            Forgot Password?
                        </button>
                    </div>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={auth.loading}
                        sx={{ py: "12px", fontSize: "0.95rem", fontWeight: 600 }}
                    >
                        {auth.loading ? <CircularProgress size={24} color="inherit" /> : "Login"}
                    </Button>
                </form>
            </Paper>

            {/* Password Reset Modal Dialog */}
            <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle className="font-bold">Reset Admin Password</DialogTitle>
                <DialogContent className="space-y-4 pt-2">
                    <p className="text-xs text-slate-500">
                        Enter your registered Admin email address to generate and send a new 10-character temporary password.
                    </p>
                    {resetMessage && (
                        <Alert severity={resetMessage.severity} sx={{ fontSize: "0.85rem" }}>
                            {resetMessage.text}
                        </Alert>
                    )}
                    <TextField
                        fullWidth
                        label="Admin Email Address"
                        type="email"
                        size="small"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                    />
                </DialogContent>
                <DialogActions className="p-4">
                    <Button onClick={() => setOpenResetDialog(false)} color="inherit" size="small">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleInitiatePasswordReset}
                        variant="contained"
                        color="primary"
                        disabled={resetLoading}
                        size="small"
                    >
                        {resetLoading ? <CircularProgress size={20} color="inherit" /> : "Reset Password"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AdminLoginForm;